import React, { useMemo } from 'react';
import { TableBody, TableCell, TableRow } from '@mui/material';
import type { DriverDbConfirmation } from '../../hooks/useDriverDatabaseDialog';
import * as S from '../../styles';
import { DIALOGS } from '../../TransportCloudTable.consts';
import { GroupedItems, Item } from '../../TransportCloudTable.types';
import { ConfirmMultiEditDialog } from '../Dialogs/ConfirmMultiEditDialog';
import { DriverDatabaseConfirmDialog } from '../Dialogs/DriversDatabase/DriverDatabaseConfirmDialog';
import { TableRowItem } from './TableRowItem';

interface TableBodyContentProps {
  groupedRows: GroupedItems[];
  loading: boolean;
  filteredHeaders: [string, string][];
  hidingColumn: string | null;
  hidingColumns: Set<string>;
  showingColumns: Set<string>;
  editingCell: { itemId: string; columnKey: string } | null;
  editingValue: any;
  recentlyEditedCell: { itemId: string; columnKey: string } | null;
  savingCells: {
    itemId: string;
    columnKey: string;
    status:
      | 'loading'
      | 'success'
      | 'error'
      | 'fading'
      | 'success-other'
      | 'fading-other';
  }[];
  validationError: {
    itemId: string;
    columnKey: string;
    errorMessage: string;
    status?: 'active' | 'fading';
  } | null;
  lockedCells: {
    rowId: number;
    fieldName: string;
    user: string;
  }[];
  currentUser: string;
  canEditAllColumns: boolean;
  hasForwarderEditPrivilege: boolean;
  hasTransportEditPrivilege: boolean;
  canEditAllRecords: boolean;
  hasOwnerEditPrivilege: boolean;
  canEditAllShipmentPlans: boolean;
  hasOwnerEditShipmentPlansPrivilege: boolean;
  ownerIds: Set<number>;
  canAddAllLoads: boolean;
  hasOwnerAddLoadPrivilege: boolean;
  dateChangedRows: Map<
    string,
    { newDate: string; status: 'pending' | 'accepted' }
  >;
  deletedRows: Set<string>;
  deletingRows: Set<string>;
  deleteErrorRows: Set<string>;
  recentlyDeletedRows: Map<string, boolean>;
  selectedItems: string[];
  multiEditConfirmation: {
    itemId: string;
    columnKey: string;
    newValue: any;
    position: { top: number; left: number };
  } | null;
  acceptMultiEditConfirmation: {
    itemId: string;
    position: { top: number; right: number };
  } | null;
  onCellEdit: (itemId: string, columnKey: string, cellValue: any) => void;
  onCellSave: (
    itemId: string,
    columnKey: string,
    newValue: any,
    skipDriverCheck?: boolean,
  ) => void;
  onCellCancel: () => void;
  onEditValueChange: (value: any) => void;
  driverDbConfirmation: DriverDbConfirmation | null;
  onDriverDbConfirm: () => void;
  onDriverDbCancel: () => void;
  onMultiEditConfirm: (persistChecked: boolean) => void;
  onMultiEditCancel: () => void;
  onMultiEditClickOutside: () => void;
  onAcceptMultiEditConfirm: (persistChecked: boolean) => void;
  onAcceptMultiEditCancel: () => void;
  onAcceptMultiEditClickOutside: () => void;
  onCheckboxClick: (itemId: string) => void;
  onSelectGroup: (itemIds: string[]) => void;
  onReload: () => void;
  onAcceptDateChange: (itemId: string) => void;
}

const TableBodyContentInner: React.FC<TableBodyContentProps> = ({
  groupedRows,
  loading,
  filteredHeaders,
  hidingColumn,
  hidingColumns,
  showingColumns,
  editingCell,
  editingValue,
  recentlyEditedCell: _recentlyEditedCell,
  savingCells,
  validationError,
  lockedCells,
  currentUser,
  canEditAllColumns,
  hasForwarderEditPrivilege,
  hasTransportEditPrivilege,
  canEditAllRecords,
  hasOwnerEditPrivilege,
  canEditAllShipmentPlans,
  hasOwnerEditShipmentPlansPrivilege,
  ownerIds,
  canAddAllLoads,
  hasOwnerAddLoadPrivilege,
  dateChangedRows,
  deletedRows,
  deletingRows,
  deleteErrorRows,
  recentlyDeletedRows,
  selectedItems,
  multiEditConfirmation,
  acceptMultiEditConfirmation,
  onCellEdit,
  onCellSave,
  onCellCancel,
  onEditValueChange,
  driverDbConfirmation,
  onDriverDbConfirm,
  onDriverDbCancel,
  onMultiEditConfirm,
  onMultiEditCancel,
  onMultiEditClickOutside,
  onAcceptMultiEditConfirm,
  onAcceptMultiEditCancel,
  onAcceptMultiEditClickOutside,
  onCheckboxClick,
  onSelectGroup,
  onReload,
  onAcceptDateChange,
}) => {
  const selectedSet = useMemo(() => new Set(selectedItems), [selectedItems]);

  const savingCellsByItem = useMemo(() => {
    const map = new Map<string, typeof savingCells>();
    savingCells.forEach((cell) => {
      const arr = map.get(cell.itemId);
      if (arr) arr.push(cell);
      else map.set(cell.itemId, [cell]);
    });
    return map;
  }, [savingCells]);

  const lockedCellsByRow = useMemo(() => {
    const map = new Map<number, typeof lockedCells>();
    lockedCells.forEach((cell) => {
      const arr = map.get(cell.rowId);
      if (arr) arr.push(cell);
      else map.set(cell.rowId, [cell]);
    });
    return map;
  }, [lockedCells]);

  const cursorPointerSx = useMemo(() => ({ cursor: 'pointer' }), []);
  const jobCodeSeparatorSx = useMemo(
    () => ({
      height: '11px',
      padding: 0,
      backgroundColor: '#f5f5f5',
      borderTop: '1px solid #e0e0e0',
      borderBottom: '1px solid #e0e0e0',
    }),
    [],
  );

  return (
    <>
      <TableBody>
        {groupedRows.map((group, groupIndex) => (
          <React.Fragment key={`group-${group.date}-${groupIndex}`}>
            <TableRow
              onDoubleClick={() => {
                const allItemsInDate: string[] = [];
                group.jobCodeGroups.forEach((jobCodeGroup) => {
                  jobCodeGroup.items.forEach((item) => {
                    const itemId = item.jobEntryId
                      ? `job-${item.jobEntryId}`
                      : item.shipmentPlanContainerId
                      ? `plan-${item.shipmentPlanContainerId}`
                      : item.lp
                      ? `lp-${item.lp}`
                      : '';
                    if (itemId && !deletedRows.has(itemId)) {
                      allItemsInDate.push(itemId);
                    }
                  });
                });
                onSelectGroup(allItemsInDate);
              }}
              sx={cursorPointerSx}
            >
              <S.StyledTableDataCell colSpan={filteredHeaders.length + 1}>
                <S.StickyDateLabel>
                  {group.weekday
                    ? `${group.weekday} ${group.date}`
                    : group.date}
                </S.StickyDateLabel>
              </S.StyledTableDataCell>
            </TableRow>
            {group.jobCodeGroups.map((jobCodeGroup, jobCodeGroupIndex) => (
              <React.Fragment
                key={`jobcode-${groupIndex}-${jobCodeGroupIndex}`}
              >
                <TableRow
                  onDoubleClick={() => {
                    const allItemsInJobCode: string[] = [];
                    jobCodeGroup.items.forEach((item) => {
                      const itemId = item.jobEntryId
                        ? `job-${item.jobEntryId}`
                        : item.shipmentPlanContainerId
                        ? `plan-${item.shipmentPlanContainerId}`
                        : item.lp
                        ? `lp-${item.lp}`
                        : '';
                      if (itemId && !deletedRows.has(itemId)) {
                        allItemsInJobCode.push(itemId);
                      }
                    });
                    onSelectGroup(allItemsInJobCode);
                  }}
                  sx={cursorPointerSx}
                >
                  <TableCell
                    colSpan={filteredHeaders.length + 1}
                    sx={jobCodeSeparatorSx}
                  />
                </TableRow>
                {jobCodeGroup.items.map((item: Item, itemIndex: number) => {
                  const itemId = item.jobEntryId
                    ? `job-${item.jobEntryId}`
                    : item.shipmentPlanContainerId
                    ? `plan-${item.shipmentPlanContainerId}`
                    : item.lp
                    ? `lp-${item.lp}`
                    : `index-${groupIndex}-${itemIndex}`;

                  const isDateChanged = dateChangedRows.has(itemId);
                  const dateChangeInfo = isDateChanged
                    ? dateChangedRows.get(itemId)
                    : null;
                  const isDeleted = deletedRows.has(itemId);
                  const isDeleting = deletingRows.has(itemId);
                  const hasDeleteError = deleteErrorRows.has(itemId);
                  const wasDeletedByCurrentUser =
                    recentlyDeletedRows.get(itemId);
                  const deletionStatus = isDeleting
                    ? 'loading'
                    : hasDeleteError
                    ? 'error'
                    : wasDeletedByCurrentUser === true
                    ? 'success'
                    : wasDeletedByCurrentUser === false
                    ? 'success-other'
                    : null;

                  const editingCellForRow =
                    editingCell?.itemId === itemId ? editingCell : null;
                  const editingValueForRow = editingCellForRow
                    ? editingValue
                    : null;
                  const validationErrorForRow =
                    validationError?.itemId === itemId ? validationError : null;

                  return (
                    <TableRowItem
                      key={`${group.date}-${
                        item.jobEntryId || `item-${itemIndex}`
                      }`}
                      item={item}
                      itemId={itemId}
                      groupDate={group.date}
                      itemIndex={itemIndex}
                      loading={loading}
                      filteredHeaders={filteredHeaders}
                      hidingColumn={hidingColumn}
                      hidingColumns={hidingColumns}
                      showingColumns={showingColumns}
                      editingCell={editingCellForRow}
                      editingValue={editingValueForRow}
                      rowSavingCells={savingCellsByItem.get(itemId)}
                      validationError={validationErrorForRow}
                      rowLockedCells={lockedCellsByRow.get(
                        item.jobEntryId || item.shipmentPlanContainerId,
                      )}
                      currentUser={currentUser}
                      canEditAllColumns={canEditAllColumns}
                      hasForwarderEditPrivilege={hasForwarderEditPrivilege}
                      hasTransportEditPrivilege={hasTransportEditPrivilege}
                      canEditAllRecords={canEditAllRecords}
                      hasOwnerEditPrivilege={hasOwnerEditPrivilege}
                      canEditAllShipmentPlans={canEditAllShipmentPlans}
                      hasOwnerEditShipmentPlansPrivilege={
                        hasOwnerEditShipmentPlansPrivilege
                      }
                      ownerIds={ownerIds}
                      canAddAllLoads={canAddAllLoads}
                      hasOwnerAddLoadPrivilege={hasOwnerAddLoadPrivilege}
                      isSelected={selectedSet.has(itemId)}
                      isDateChanged={isDateChanged}
                      dateChangeInfo={dateChangeInfo}
                      isDeleted={isDeleted}
                      deletionStatus={deletionStatus}
                      onCellEdit={onCellEdit}
                      onCellSave={onCellSave}
                      onCellCancel={onCellCancel}
                      onEditValueChange={onEditValueChange}
                      onCheckboxClick={onCheckboxClick}
                      onReload={onReload}
                      onAcceptDateChange={onAcceptDateChange}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </TableBody>
      <ConfirmMultiEditDialog
        open={!!multiEditConfirmation}
        position={multiEditConfirmation?.position || { top: 0, left: 0 }}
        itemId={multiEditConfirmation?.itemId || ''}
        columnKey={multiEditConfirmation?.columnKey || ''}
        onConfirm={onMultiEditConfirm}
        onCancel={onMultiEditCancel}
        onClickOutside={onMultiEditClickOutside}
      />
      <ConfirmMultiEditDialog
        open={!!acceptMultiEditConfirmation}
        position={acceptMultiEditConfirmation?.position || { top: 0, right: 0 }}
        itemId={acceptMultiEditConfirmation?.itemId || ''}
        columnKey='loadingDeliveryDate'
        align='right'
        anchorSelector={
          acceptMultiEditConfirmation
            ? `[data-item-id="${acceptMultiEditConfirmation.itemId}"][data-warning-icon="dateChange"]`
            : undefined
        }
        confirmationText={DIALOGS.acceptMultiEditingConfirmation}
        onConfirm={onAcceptMultiEditConfirm}
        onCancel={onAcceptMultiEditCancel}
        onClickOutside={onAcceptMultiEditClickOutside}
      />
      <DriverDatabaseConfirmDialog
        open={!!driverDbConfirmation}
        mode={driverDbConfirmation?.mode ?? 'add'}
        values={
          driverDbConfirmation?.values ?? {
            driverName: '',
            driverDocumentNumber: '',
            driverPhone: '',
          }
        }
        existingValues={driverDbConfirmation?.existingValues}
        itemId={driverDbConfirmation?.itemId ?? ''}
        columnKey={driverDbConfirmation?.columnKey ?? ''}
        onConfirm={onDriverDbConfirm}
        onCancel={onDriverDbCancel}
      />
    </>
  );
};

export const TableBodyContent = React.memo(TableBodyContentInner);
